import React, { useMemo, useState } from "react";

import "./SpiderChartStyles.scss";
import "../ChartFrame.scss";
import ChartTooltip from "../ChartTooltip";
import { CHART_COLORS, withAlpha } from "../chartPalette";
import { CHART_THEME } from "../chartTheme";
import { polarPoint, radarPolygon, truncateLabel } from "../svg/helpers";

export default function SpiderChart({ domains }) {
  const [hover, setHover] = useState(null);

  const { labels, values, size } = useMemo(() => {
    const labs = [];
    const vals = [];
    (domains || []).forEach((d) => {
      labs.push(d.domain);
      vals.push(parseInt(d.percentage_completed, 10) || 0);
    });
    return { labels: labs, values: vals, size: 340 };
  }, [domains]);

  // Horizontal padding in the viewBox so the outer domain labels (which extend
  // well beyond the plot radius) are not clipped at the frame edge.
  const padX = 96;
  const cx = size / 2;
  const cy = size / 2 + 8;
  const maxR = size * 0.4;
  const rings = [25, 50, 75, 100];
  const n = Math.max(labels.length, 1);
  const { points, path } = radarPolygon(values, cx, cy, maxR, 100);

  const labelPoints = labels.map((_, i) => {
    const angle = (Math.PI * 2 * i) / n;
    return polarPoint(cx, cy, maxR + 28, angle);
  });

  return (
    <div className="radar cb-chart-frame cb-chart-frame--radar cb-spider-chart">
      <svg
        viewBox={`${-padX} 0 ${size + padX * 2} ${size + 24}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Domain completion radar"
      >

        {rings.map((pct) => (
          <circle
            key={pct}
            cx={cx}
            cy={cy}
            r={(maxR * pct) / 100}
            fill="none"
            stroke={CHART_THEME.grid}
            strokeWidth={1}
            strokeDasharray="3 5"
          />
        ))}

        {labels.map((_, i) => {
          const angle = (Math.PI * 2 * i) / n;
          const tip = polarPoint(cx, cy, maxR, angle);
          return (
            <line
              key={`spoke-${i}`}
              x1={cx}
              y1={cy}
              x2={tip.x}
              y2={tip.y}
              stroke={CHART_THEME.grid}
              strokeWidth={1}
            />
          );
        })}

        {rings.map((pct) => (
          <text
            key={`ring-lab-${pct}`}
            className="cb-chart-frame__axis-label"
            x={cx + 4}
            y={cy - (maxR * pct) / 100 + 3}
          >
            {pct}
          </text>
        ))}

        <path
          d={path}
          fill={withAlpha(CHART_COLORS[0], 0.12)}
          stroke={CHART_COLORS[0]}
          strokeWidth={2.75}
          strokeLinejoin="round"
        />

        {points.map((p, i) => (
          <circle
            key={`pt-${i}`}
            cx={p.x}
            cy={p.y}
            r={hover?.i === i ? 6.5 : 5}
            fill="#fff"
            stroke={CHART_COLORS[0]}
            strokeWidth={2.5}
            style={{ cursor: "pointer" }}
            onMouseEnter={(e) => {
              const rect = e.currentTarget
                .closest(".cb-chart-frame")
                ?.getBoundingClientRect();
              setHover({
                i,
                x: e.clientX - (rect?.left || 0),
                y: e.clientY - (rect?.top || 0),
                label: labels[i],
                value: values[i],
                color: CHART_COLORS[0],
              });
            }}
            onMouseLeave={() => setHover(null)}
          />
        ))}

        {labels.map((lab, i) => {
          const lp = labelPoints[i];
          const angle = (Math.PI * 2 * i) / n;
          const anchor =
            Math.abs(Math.sin(angle)) < 0.2
              ? "middle"
              : Math.sin(angle) > 0
              ? "start"
              : "end";
          return (
            <text
              key={`lab-${i}`}
              className="cb-chart-frame__tick-label"
              x={lp.x}
              y={lp.y}
              textAnchor={anchor}
              dominantBaseline="middle"
            >
              {truncateLabel(lab, 16)}
            </text>
          );
        })}
      </svg>

      <ChartTooltip x={hover?.x || 0} y={hover?.y || 0} visible={!!hover}>
        {hover && (
          <>
            <span className="cb-chart-tooltip__title">{hover.label}</span>
            <span className="cb-chart-tooltip__row">
              <span
                className="cb-chart-tooltip__dot"
                style={{ background: hover.color }}
              />
              <strong>{hover.value}%</strong> complete
            </span>
          </>
        )}
      </ChartTooltip>
    </div>
  );
}
